import { useQueryClient } from '@tanstack/react-query';
import { parseHttpError } from '@upme/api-client';
import type { AdminUserDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Coins } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { formatEnergyDelta, formatEnergyFull } from '../../../shared/format-energy';
import { useAdjustCoins } from '../../coin-transactions/mutations';
import { usersKey } from '../queries';

// `POST /admin/coins/adjust` пишет только PENDING-строку: `User.coinBalance`
// инкрементит CoinsConfirmProcessor в worker-scheduler. Рефетч сразу после
// ответа гонится с воркером и обычно приносит ещё старый баланс, поэтому
// повторяем инвалидацию с задержкой, когда confirm уже прошёл.
const RECONCILE_DELAY_MS = 2500;

interface Props {
  user: AdminUserDto;
}

export function SendEnergySection({ user }: Props) {
  const [amount, setAmount] = useState('');
  const adjust = useAdjustCoins();
  const qc = useQueryClient();
  const reconcileTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (reconcileTimer.current) clearTimeout(reconcileTimer.current);
    },
    [],
  );

  async function submit() {
    const value = Number(amount);
    if (amount.trim() === '' || !Number.isInteger(value) || value <= 0) {
      toast.error('Сумма должна быть целым числом больше 0');
      return;
    }

    try {
      const res = await adjust.mutateAsync({ userId: user.id, delta: value });
      toast.success(
        `Отправлено ${formatEnergyDelta(res.delta)} энергии — баланс обновится через несколько секунд`,
      );
      setAmount('');
      await qc.invalidateQueries({ queryKey: [...usersKey, user.id] });
      if (reconcileTimer.current) clearTimeout(reconcileTimer.current);
      reconcileTimer.current = setTimeout(() => {
        void qc.invalidateQueries({ queryKey: [...usersKey, user.id] });
      }, RECONCILE_DELAY_MS);
    } catch (err) {
      const api = await parseHttpError(err);
      toast.error(api?.message ?? 'Не удалось отправить энергию');
    }
  }

  return (
    <div className="rounded-lg border bg-card p-6 space-y-3">
      <h2 className="font-medium">Энергия</h2>
      <p className="text-sm text-muted-foreground">
        Доступно: {formatEnergyFull(user.energyAvailable)} ⚡
        {user.energyAvailable !== user.coinBalance ? (
          <span className="ml-1 text-xs">(ledger: {formatEnergyFull(user.coinBalance)})</span>
        ) : null}
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="space-y-1.5 flex-1">
          <Label htmlFor="send-energy-amount">Сумма</Label>
          <Input
            id="send-energy-amount"
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            placeholder="100"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <Button onClick={submit} disabled={adjust.isPending}>
          <Coins className="mr-2 h-4 w-4" />
          Отправить энергию
        </Button>
      </div>
    </div>
  );
}
